import type { DashboardTemplate, ResponsiveLayouts, WidgetInstance } from '@/types';
import { newId } from '@/utils';

function makeLayouts(
  items: Array<{ i: string; x: number; y: number; w: number; h: number }>,
): ResponsiveLayouts {
  const clone = () => items.map((it) => ({ ...it }));
  return { lg: clone(), md: clone(), sm: clone(), xs: clone(), xxs: clone() };
}

function build(
  widgets: WidgetInstance[],
  grid: Array<{ x: number; y: number; w: number; h: number }>,
) {
  const layouts = makeLayouts(widgets.map((w, i) => ({ i: w.id, ...grid[i] })));
  return { widgets, layouts };
}

export const dashboardTemplates: DashboardTemplate[] = [
  {
    key: 'executive',
    name: 'Executive Dashboard',
    description: 'High-level KPIs and revenue trends for executives',
    icon: 'FundOutlined',
    build: () => {
      const widgets: WidgetInstance[] = [
        {
          id: newId('w'),
          type: 'kpi',
          title: 'Total Revenue',
          config: { value: 1240000, unit: '$', delta: 12.4, format: 'currency' },
        },
        {
          id: newId('w'),
          type: 'kpi',
          title: 'Active Users',
          config: { value: 24812, delta: 5.1, format: 'compact' },
        },
        {
          id: newId('w'),
          type: 'kpi',
          title: 'Conversion',
          config: { value: 3.4, delta: -0.6, format: 'percent' },
        },
        {
          id: newId('w'),
          type: 'kpi',
          title: 'NPS',
          config: { value: 58, delta: 4, format: 'number' },
        },
        { id: newId('w'), type: 'line', title: 'Revenue Trend', config: {} },
        { id: newId('w'), type: 'pie', title: 'Traffic Sources', config: {} },
      ];
      return {
        name: 'Executive Dashboard',
        description: 'Executive KPIs',
        tags: ['executive'],
        ...build(widgets, [
          { x: 0, y: 0, w: 3, h: 3 },
          { x: 3, y: 0, w: 3, h: 3 },
          { x: 6, y: 0, w: 3, h: 3 },
          { x: 9, y: 0, w: 3, h: 3 },
          { x: 0, y: 3, w: 8, h: 6 },
          { x: 8, y: 3, w: 4, h: 6 },
        ]),
      };
    },
  },
  {
    key: 'sales',
    name: 'Sales Dashboard',
    description: 'Pipeline, deals, and quotas',
    icon: 'DollarOutlined',
    build: () => {
      const widgets: WidgetInstance[] = [
        {
          id: newId('w'),
          type: 'kpi',
          title: 'Pipeline',
          config: { value: 3420000, format: 'currency', delta: 8.2 },
        },
        {
          id: newId('w'),
          type: 'kpi',
          title: 'Won Deals',
          config: { value: 128, format: 'number', delta: 3 },
        },
        {
          id: newId('w'),
          type: 'kpi',
          title: 'Win Rate',
          config: { value: 0.42, format: 'percent', delta: 2.1 },
        },
        { id: newId('w'), type: 'bar', title: 'Sales by Rep', config: {} },
        { id: newId('w'), type: 'area', title: 'Weekly Bookings', config: {} },
        { id: newId('w'), type: 'table', title: 'Recent Deals', config: {} },
      ];
      return {
        name: 'Sales Dashboard',
        description: 'Sales metrics',
        tags: ['sales'],
        ...build(widgets, [
          { x: 0, y: 0, w: 4, h: 3 },
          { x: 4, y: 0, w: 4, h: 3 },
          { x: 8, y: 0, w: 4, h: 3 },
          { x: 0, y: 3, w: 6, h: 6 },
          { x: 6, y: 3, w: 6, h: 6 },
          { x: 0, y: 9, w: 12, h: 7 },
        ]),
      };
    },
  },
  {
    key: 'operations',
    name: 'Operations Dashboard',
    description: 'System health and throughput',
    icon: 'SettingOutlined',
    build: () => {
      const widgets: WidgetInstance[] = [
        { id: newId('w'), type: 'gauge', title: 'CPU Load', config: { value: 62, max: 100 } },
        { id: newId('w'), type: 'gauge', title: 'Memory Usage', config: { value: 78, max: 100 } },
        { id: newId('w'), type: 'gauge', title: 'Error Rate', config: { value: 1.2, max: 10 } },
        { id: newId('w'), type: 'line', title: 'Requests / Minute', config: {} },
        { id: newId('w'), type: 'activity', title: 'System Events', config: {} },
      ];
      return {
        name: 'Operations Dashboard',
        description: 'Ops metrics',
        tags: ['ops'],
        ...build(widgets, [
          { x: 0, y: 0, w: 4, h: 4 },
          { x: 4, y: 0, w: 4, h: 4 },
          { x: 8, y: 0, w: 4, h: 4 },
          { x: 0, y: 4, w: 8, h: 6 },
          { x: 8, y: 4, w: 4, h: 6 },
        ]),
      };
    },
  },
  {
    key: 'finance',
    name: 'Finance Dashboard',
    description: 'Cash flow, expenses, forecasting',
    icon: 'BankOutlined',
    build: () => {
      const widgets: WidgetInstance[] = [
        {
          id: newId('w'),
          type: 'kpi',
          title: 'Cash Balance',
          config: { value: 8432000, format: 'currency', delta: 1.8 },
        },
        {
          id: newId('w'),
          type: 'kpi',
          title: 'Monthly Burn',
          config: { value: 320000, format: 'currency', delta: -2.5 },
        },
        {
          id: newId('w'),
          type: 'kpi',
          title: 'Runway (mo)',
          config: { value: 26, format: 'number', delta: 1 },
        },
        { id: newId('w'), type: 'area', title: 'Cash Flow', config: {} },
        { id: newId('w'), type: 'pie', title: 'Expense Breakdown', config: {} },
      ];
      return {
        name: 'Finance Dashboard',
        description: 'Finance metrics',
        tags: ['finance'],
        ...build(widgets, [
          { x: 0, y: 0, w: 4, h: 3 },
          { x: 4, y: 0, w: 4, h: 3 },
          { x: 8, y: 0, w: 4, h: 3 },
          { x: 0, y: 3, w: 8, h: 6 },
          { x: 8, y: 3, w: 4, h: 6 },
        ]),
      };
    },
  },
  {
    key: 'customer_success',
    name: 'Customer Success Dashboard',
    description: 'Churn, satisfaction, ticket volume',
    icon: 'SmileOutlined',
    build: () => {
      const widgets: WidgetInstance[] = [
        {
          id: newId('w'),
          type: 'kpi',
          title: 'CSAT',
          config: { value: 92, format: 'number', delta: 1.4 },
        },
        {
          id: newId('w'),
          type: 'kpi',
          title: 'Churn (30d)',
          config: { value: 0.021, format: 'percent', delta: -0.3 },
        },
        {
          id: newId('w'),
          type: 'kpi',
          title: 'Open Tickets',
          config: { value: 47, format: 'number', delta: -12 },
        },
        { id: newId('w'), type: 'bar', title: 'Tickets by Category', config: {} },
        { id: newId('w'), type: 'notification', title: 'Recent Alerts', config: {} },
      ];
      return {
        name: 'Customer Success Dashboard',
        description: 'CS metrics',
        tags: ['cs'],
        ...build(widgets, [
          { x: 0, y: 0, w: 4, h: 3 },
          { x: 4, y: 0, w: 4, h: 3 },
          { x: 8, y: 0, w: 4, h: 3 },
          { x: 0, y: 3, w: 8, h: 6 },
          { x: 8, y: 3, w: 4, h: 6 },
        ]),
      };
    },
  },
];
